
import { defineComponent, nextTick, ref } from "vue";
import { ElAutocomplete, ElInput } from "element-plus";
import { useRoute } from "vue-router";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import {
  getErrorMsg,
  instanceOfIVatTuWithSoLuongDaXuat,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import {
  ICapSuaChuaResponseBase,
  IDinhMucCoDinhNewResponseBase,
  IKeHoachSuaChuaResponseBase,
  IVatTuResponseBase,
  IVatTuWithSoLuongDaXuat,
} from "@/core/interfaces/ApiResponses";
import { KeHoachSuaChuaService } from "@/core/services/KeHoachSuaChua.service";
import { KeHoachNamService } from "@/core/services/KeHoachNam.service";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { AddVatTuItemsFor } from "@/core/enums/addVatTuItemsFor.enum";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useLoading } from "@/core/hooks/use-loading";
import { BienBanGiaiTheService } from "@/core/services/BienBanGiaiThe.service";
import { useDateTime, getGlobalDefaultDate, formatDatePayload } from "@/core/hooks/use-date-time-hook";
import { useBienBan } from "@/core/hooks/use-bien-ban-hook";
import { CapSuaChuaService } from "@/core/services/CapSuaChuaService.service";
import { DinhMucCoDinhService } from "@/core/services/DinhMucCoDinh.service";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import AddVatTuItemV2 from "@/components/add-vat-tu-items-v2/AddVatTuItemV2.vue";
import AddVatTuTableItems from "@/components/add-vat-tu-items-v2/add-vat-tu-table-items/AddVatTuTableItems.vue";
import ToaXeService from "@/core/services/ToaXe.service";
import ResponsiveLayout from "@/layout/_shared/ResponsiveLayout.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import useFormInput from "@/core/hooks/use-form-input";
import { mapMutations } from "vuex";

const BBGT_EDIT_APPROVAL_ALERT_BASE =
  "Mọi thay cập nhật trong Biên bản giải thể sẽ cần được phê duyệt lại";
const BBGT_EDIT_APPROVAL_ALERT_FULLY_ISSUED =
  "Số lượng vật tư Ngoài Định Mức đã xuất hết";

type ToaXeAutocompleteInstance = InstanceType<typeof ElAutocomplete> & {
  activated: boolean;
  suggestionDisabled: boolean;
  inputRef?: InstanceType<typeof ElInput>;
};

export default defineComponent({
  name: "bien-ban-giai-the-form",

  components: {
    AddVatTuItemV2,
    AddVatTuTableItems,
    BaseLink,
    ButtonCustom,
    DivCol,
    DivRow,
    LabelCustom,
    PageContentBase,
    ResponsiveLayout,
  },

  setup() {
    const { formRef } = useCreatingForm();
    const { focusFirstErrInputV2 } = useFormInput();

    const {
      params: { idKeHoach, idBienBan },
      name,
    } = useRoute();

    const { goBack, push, replace, searchInUri } = useRouterCustom();
    const { isCreating } = useCreatingForm();
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();
    const { startLoading, endLoading } = useLoading();
    const { moment } = useDateTime();
    const { approvalLevel } = useBienBan();
    const toaXeSearchRef = ref<ToaXeAutocompleteInstance | null>(null);
    const capSuaChuaSelectRef = ref(null);
    const ngayVaoXuongPickerRef = ref(null);
    const dinhMucCoDinhDetail = ref<null | IDinhMucCoDinhNewResponseBase>(null);

    return {
      idKeHoach: idKeHoach ? +idKeHoach : 0,
      idBienBan: idBienBan ? +idBienBan : 0,
      approvalLevel,
      dinhMucCoDinhDetail,
      goBack,
      push,
      replace,
      searchInUri,
      routeName: name,
      formRef,
      toaXeSearchRef,
      capSuaChuaSelectRef,
      ngayVaoXuongPickerRef,
      AddVatTuItemsFor,
      ButtonTypeColors,
      ButtonsType,
      startLoading,
      endLoading,
      moment,
      isCreating,
      focusFirstErrInputV2,
    };
  },

  data() {
    const capSuaChuaValidation = (_rule, value, fn) => {
      if (!value) {
        fn(new Error("Bắt buộc phải chọn cấp sửa chữa"));
      } else {
        fn();
      }
    };

    return {
      capSuaChuaItems: [] as ICapSuaChuaResponseBase[],
      keHoachDetail: null as IKeHoachSuaChuaResponseBase | null,
      form: {
        so_bien_ban: "",
        ngay_vao_xuong: getGlobalDefaultDate(),
        has_dinh_muc_dong: false,
        cap_sua_chua_id: 0,
        ten_toa_xe: "",
      },
      danhMucToaXeId: 0,
      tenDanhMucToaXe: "",
      isDanhMucItemsAvailable: true,
      isBelongedToKeHoach: true,
      isKeHoachNam: false,
      vatTuItems: [] as IVatTuWithSoLuongDaXuat[],
      isDinhMucDongModalOpen: false,
      tenToaXe: "",
      toaXeId: 0,
      soHieuToaXe: "",
      isFoundToaXe: true,
      soBienBanGenerationToken: 0,
      soBienBanGenerationReady: false,
      rules: {
        ten_toa_xe: [
          {
            required: true,
            message: "Toa xe không được bỏ trống",
            trigger: "change",
          },
        ],
        so_bien_ban: [
          {
            required: true,
            message: "Số biên bản chưa được tạo",
            trigger: "change",
          },
        ],
        ngay_vao_xuong: [
          {
            required: true,
            message: "Bạn phải nhập ngày vào xưởng",
            trigger: "change",
          },
        ],
        cap_sua_chua_id: [
          {
            validator: capSuaChuaValidation,
            trigger: "blur",
          },
        ],
      },
      soBienBanParent: "",
      ngayRaXuongParent: null as Date | string | null,
    };
  },

  created() {
    this.rules.ngay_vao_xuong.push({
      validator: this.ngayVaoXuongValidation,
      trigger: "change",
    });
  },

  watch: {
    soBienBanGenerationDeps() {
      if (this.soBienBanGenerationReady) {
        this.generateSoBienBanNumber();
      }
    },

    async capSuaChuaAndDanhMucToaXe([newCapSuaChuaId, newDanhMucToaXeId]) {
      if (!newCapSuaChuaId || !newDanhMucToaXeId) {
        this.dinhMucCoDinhDetail = null;

        return;
      }

      const dinhMucResponse =
        await DinhMucCoDinhService.getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
          id_cap_sc: newCapSuaChuaId,
          id_dm_toa_xe: newDanhMucToaXeId,
        });

      this.dinhMucCoDinhDetail = dinhMucResponse;
    },
  },

  computed: {
    formTitle() {
      return this.isCreating ? "Tạo mới" : "Cập nhật";
    },
    isCreatingBienBanGiaiTheBoSung() {
      return this.routeName === "tao-bbgt-bo-sung";
    },
    isSearchForToaXeInputDisabled(): boolean {
      return !this.isCreating || this.isCreatingBienBanGiaiTheBoSung;
    },
    capSuaChuaAndDanhMucToaXe() {
      return [this.form.cap_sua_chua_id, this.danhMucToaXeId];
    },
    capSuaChuaAndDmToaXeAvailable() {
      const capScId =
        this.isBelongedToKeHoach && this.keHoachDetail
          ? this.keHoachDetail.id_cap_sc
          : this.form.cap_sua_chua_id;

      return !!capScId && !!this.danhMucToaXeId;
    },
    soBienBanGenerationDeps() {
      const capScId =
        this.isBelongedToKeHoach && this.keHoachDetail
          ? this.keHoachDetail.id_cap_sc
          : this.form.cap_sua_chua_id;

      const month = this.form.ngay_vao_xuong
        ? this.moment(this.form.ngay_vao_xuong).format("MM")
        : "";

      return [
        month,
        capScId,
        this.toaXeId,
        this.soHieuToaXe,
        this.isCreatingBienBanGiaiTheBoSung ? this.idBienBan : 0,
      ];
    },
    isAllNgoaiDinhMucFullyIssued(): boolean {
      if (!this.vatTuItems.length) {
        return false;
      }

      return this.vatTuItems.every((item) => {
        const soLuongBanDau = +(item.pivot?.so_luong ?? item.so_luong ?? 0);
        const soLuongDaXuat = +(item.so_luong_da_xuat_kho ?? 0);

        return soLuongBanDau - soLuongDaXuat === 0;
      });
    },
    editApprovalAlertMessage(): string {
      if (this.isCreating || this.approvalLevel <= 0) {
        return "";
      }

      if (this.isAllNgoaiDinhMucFullyIssued) {
        return `${BBGT_EDIT_APPROVAL_ALERT_FULLY_ISSUED}. ${BBGT_EDIT_APPROVAL_ALERT_BASE}`;
      }

      return BBGT_EDIT_APPROVAL_ALERT_BASE;
    },
  },

  async mounted() {
    this.isKeHoachNam = this.searchInUri("ke-hoach-nam");
    this.isBelongedToKeHoach =
      this.searchInUri("ke-hoach-sua-chua") || this.isKeHoachNam;

    if (this.isBelongedToKeHoach) {
      setBreadcrumbLinks([
        {
          text: this.isKeHoachNam ? "Kế Hoạch SC năm" : "Kế Hoạch SC",
          routeData: {
            name: this.isKeHoachNam
              ? "danh-sach-ke-hoach-nam"
              : "danh-sach-ke-hoach-sua-chua",
          },
        },
        {
          text: this.isKeHoachNam ? "Kế Hoạch SC năm" : "Kế Hoạch SC",
          routeData: {
            name: this.isKeHoachNam
              ? "chi-tiet-ke-hoach-nam"
              : "chi-tiet-ke-hoach-sua-chua",
            params: {
              id: this.idKeHoach,
            },
          },
        },
        ...(this.isCreating
          ? []
          : [
              {
                text: "BBGT",
                routeData: {
                  name: "chi-tiet-bien-ban-giai-the",
                  params: {
                    id: this.idBienBan,
                  },
                },
              },
            ]),
        {
          text: this.isCreating ? "Tạo mới" : "Cập nhật",
        },
      ]);
    } else {
      setBreadcrumbLinks([
        {
          text: "Biên bản giải thể",
          routeData: {
            name: "danh-sach-bien-ban-giai-the",
          },
        },
        {
          text: this.isCreating ? "Tạo mới" : "Sửa BBGT",
        },
      ]);
    }

    await this.startLoading();

    try {
      if (!this.isBelongedToKeHoach) {
        await this.fetchCapSuaChuaItems(false);
      }

      if (this.isCreatingBienBanGiaiTheBoSung) {
        await this.fetchBienBan(false);
        await this.fetchToaXe(false);
      }

      if (this.isCreating && this.isBelongedToKeHoach) {
        await this.fetchKeHoach(false);
      } else if (!this.isCreating) {
        await this.fetchBienBan(false);

        if (this.isBelongedToKeHoach) {
          await this.fetchKeHoach(false);
        }

        await this.fetchToaXe(false);
      }

      setCurrentPageTitle(
        this.isCreating
          ? "Tạo mới biên bản giải thể"
          : `BBGT số: ${this.form.so_bien_ban}`
      );

      document.title = this.isCreating
        ? "Tạo mới - Biên bản giải thể - Đường sắt Hà Nội"
        : `${this.form.so_bien_ban} - Cập nhật - Biên bản giải thể - Đường sắt Hà Nội`;
    } catch (error) {
      console.error("Error during initialization:", error);
    } finally {
      this.soBienBanGenerationReady = true;

      if (this.isCreatingBienBanGiaiTheBoSung) {
        await this.generateSoBienBanNumber();
      }

      await this.endLoading();
      await this.focusToaXeInput();
    }
  },

  methods: {
    ...mapMutations("ConfirmationDialogModule", {
      setDialog: "setDialog",
      closeDialog: "closeDialog",
    }),

    async focusToaXeInput() {
      if (this.isSearchForToaXeInputDisabled) {
        return;
      }

      await this.$nextTick();

      this.toaXeSearchRef?.focus?.();
      this.toaXeSearchRef?.inputRef?.select?.();
    },

    async focusCapSuaChuaSelect() {
      if (this.isBelongedToKeHoach || this.isCreatingBienBanGiaiTheBoSung) {
        return;
      }

      await this.$nextTick();

      this.capSuaChuaSelectRef?.focus?.();
    },

    async focusNgayVaoXuongPicker() {
      if (!this.isCreating) {
        return;
      }

      await this.$nextTick();

      this.ngayVaoXuongPickerRef?.focus?.();
    },

    toggleNgoaiDinhMuc() {
      if (!this.isCreating && !this.form.can_edit_dmd) {
        return;
      }

      this.form.has_dinh_muc_dong = !this.form.has_dinh_muc_dong;
    },

    onSelectDanhMucToaXe({ id }) {
      this.danhMucToaXeId = id;
    },

    ngayVaoXuongValidation(_rule, value, callback) {
      if (!this.isCreatingBienBanGiaiTheBoSung || !value) {
        callback();

        return;
      }

      if (!this.ngayRaXuongParent) {
        callback(new Error("Biên bản cha chưa có ngày ra xưởng"));

        return;
      }

      const selectedDate = this.moment(value).startOf("day");
      const minDate = this.moment(this.ngayRaXuongParent).startOf("day");

      if (selectedDate.isBefore(minDate)) {
        callback(
          new Error(
            `Ngày vào xưởng phải từ ${minDate.format("DD/MM/YYYY")} trở đi`
          )
        );

        return;
      }

      callback();
    },

    disabledNgayVaoXuongDate(date: Date) {
      if (!this.isCreatingBienBanGiaiTheBoSung || !this.ngayRaXuongParent) {
        return false;
      }

      return this.moment(date)
        .startOf("day")
        .isBefore(this.moment(this.ngayRaXuongParent).startOf("day"));
    },

    async fetchCapSuaChuaItems(manageLoading = true) {
      try {
        if (manageLoading) {
          this.startLoading();
        }

        const {
          data: {
            data: { data },
          },
        } = await CapSuaChuaService.list({});

        this.capSuaChuaItems = data;

        if (manageLoading) {
          this.endLoading();
        }
      } catch (e) {
        if (manageLoading) {
          this.endLoading();
        }
        swalNotification(
          getErrorMsg(e, "Có lỗi khi lấy thông tin cấp sửa chữa"),
          "error"
        );
      }
    },

    getToaXeSearchDanhMucFilter(): number | null {
      return this.keHoachDetail?.id_danh_muc_toa_xe || null;
    },

    resetToaXeSelection({ keepInput = false } = {}) {
      this.toaXeId = 0;
      this.soHieuToaXe = "";
      this.tenToaXe = "";
      this.danhMucToaXeId = 0;
      this.tenDanhMucToaXe = "";
      this.form.so_bien_ban = "";

      if (!keepInput) {
        this.form.ten_toa_xe = "";
      }
    },

    onToaXeInput(value: string) {
      if (!value) {
        this.resetToaXeSelection();

        return;
      }

      if (this.toaXeId && value !== this.tenToaXe) {
        this.resetToaXeSelection({ keepInput: true });
      }
    },

    onClearToaXe() {
      this.resetToaXeSelection();
      this.reopenToaXeSuggestionsAfterClear();
    },

    reopenToaXeSuggestionsAfterClear() {
      nextTick(() => {
        const autocomplete = this.toaXeSearchRef;

        if (!autocomplete) {
          return;
        }

        autocomplete.suggestionDisabled = false;
        autocomplete.activated = true;
        autocomplete.getData("");
        autocomplete.focus();
      });
    },

    async queryToaXe(term: string, callback): Promise<void> {
      const {
        data: {
          data: { data },
        },
      } = await ToaXeService.list(
        1,
        200,
        this.getToaXeSearchDanhMucFilter(),
        term
      );

      if (!data.length) {
        this.isFoundToaXe = false;
      } else {
        this.isFoundToaXe = true;
      }

      callback(data);
    },

    onSelectToaXe({ id, ten_toa_xe, ma_toa_xe, so_hieu, danh_muc_toa_xe }) {
      this.toaXeId = id;
      this.soHieuToaXe = so_hieu || ma_toa_xe;

      this.tenToaXe = `${ma_toa_xe} - ${ten_toa_xe}`;

      this.form.ten_toa_xe = `${ma_toa_xe} - ${ten_toa_xe}`;

      if (danh_muc_toa_xe.length) {
        this.tenDanhMucToaXe = danh_muc_toa_xe[0].ten_danh_muc;

        this.danhMucToaXeId = danh_muc_toa_xe[0].id;
      }
    },

    getCapSuaChuaParam(): number | string | null {
      const capScId =
        this.isBelongedToKeHoach && this.keHoachDetail
          ? this.keHoachDetail.id_cap_sc
          : this.form.cap_sua_chua_id;

      if (!capScId) {
        return null;
      }

      if (
        this.isBelongedToKeHoach &&
        this.keHoachDetail?.cap_sua_chua?.ten_viet_tat
      ) {
        return this.keHoachDetail.cap_sua_chua.ten_viet_tat;
      }

      const capSuaChua = this.capSuaChuaItems.find(
        (item) => item.id === capScId
      );

      return capSuaChua?.ten_viet_tat || capScId;
    },

    async generateSoBienBanNumber() {
      const month = this.form.ngay_vao_xuong
        ? this.moment(this.form.ngay_vao_xuong).format("MM")
        : "";
      const capsuachua = this.getCapSuaChuaParam();
      const so_hieu_toa_xe = this.soHieuToaXe || this.toaXeId || null;

      if (!month || !capsuachua || !so_hieu_toa_xe) {
        if (this.isCreating || this.isCreatingBienBanGiaiTheBoSung) {
          this.form.so_bien_ban = "";
        }

        return;
      }

      const generationToken = ++this.soBienBanGenerationToken;

      try {
        const {
          data: {
            data: { so_bien_ban },
          },
        } = await BienBanGiaiTheService.generateSoBienBan({
          month,
          capsuachua,
          so_hieu_toa_xe,
          ...(this.isCreatingBienBanGiaiTheBoSung &&
            this.idBienBan && {
              id_bbgt_cha: this.idBienBan,
            }),
        });

        if (generationToken !== this.soBienBanGenerationToken) {
          return;
        }

        this.form.so_bien_ban = so_bien_ban;
      } catch (e) {
        if (generationToken !== this.soBienBanGenerationToken) {
          return;
        }

        if (this.isCreating || this.isCreatingBienBanGiaiTheBoSung) {
          this.form.so_bien_ban = "";
        }

        swalNotification(getErrorMsg(e, "Có lỗi khi tạo số biên bản"), "error");
      }
    },

    async fetchKeHoach(manageLoading = true) {
      try {
        const {
          data: { data },
        } = this.isKeHoachNam
          ? await KeHoachNamService.get(this.idKeHoach)
          : await KeHoachSuaChuaService.get(this.idKeHoach);
        this.keHoachDetail = data;
      } catch (e) {
        if (manageLoading) {
          await this.endLoading();
        }

        await swalNotification(
          getErrorMsg(e, "Có lỗi khi lấy thông tin kế hoạch"),
          "error"
        );
      }
    },

    async fetchBienBan(manageLoading = true) {
      try {
        const {
          data: {
            data: {
              has_dinh_muc_dong,
              so_bien_ban,
              id_ke_hoach_sua_chua,
              id_toa_xe,
              ngay_vao_xuong,
              ngay_ra_xuong,
              ngoai_dinh_muc: vatTuNgoaiDinhMucItems,
              id_cap_sc,
              ke_hoach_sua_chua,
              // phieu_xuat_kho,
              can_edit_dmd,
            },
          },
        } = await BienBanGiaiTheService.get(this.idBienBan);

        this.form.has_dinh_muc_dong = has_dinh_muc_dong;
        this.form.can_edit_dmd = can_edit_dmd;
        this.form.so_bien_ban = so_bien_ban;
        this.idKeHoach = id_ke_hoach_sua_chua;
        this.toaXeId = id_toa_xe;
        this.form.ngay_vao_xuong = ngay_vao_xuong;
        this.form.cap_sua_chua_id =
          id_cap_sc || ke_hoach_sua_chua?.id_cap_sc || 0;

        if (id_ke_hoach_sua_chua) this.isBelongedToKeHoach = true;

        if (this.isCreatingBienBanGiaiTheBoSung) {
          this.form.has_dinh_muc_dong = false;
          this.form.so_bien_ban = "";
          this.vatTuItems = [];
          this.soBienBanParent = so_bien_ban;
          this.ngayRaXuongParent = ngay_ra_xuong;

          const minNgayVaoXuong = ngay_ra_xuong
            ? this.moment(ngay_ra_xuong).startOf("day")
            : null;
          const today = this.moment(getGlobalDefaultDate()).startOf("day");

          this.form.ngay_vao_xuong =
            minNgayVaoXuong && today.isBefore(minNgayVaoXuong)
              ? minNgayVaoXuong.toDate()
              : getGlobalDefaultDate();

          return;
        }

        this.vatTuItems = vatTuNgoaiDinhMucItems.map((item) => {
          const so_luong_ban_dau = item.pivot.so_luong;

          let soLuongDaXuat = item.so_luong_da_xuat_kho;

          return {
            ...item,
            ...(item.pivot.ghi_chu && {
              ghi_chu: item.pivot.ghi_chu,
            }),
            so_luong: so_luong_ban_dau - soLuongDaXuat,
            so_luong_da_xuat_kho: soLuongDaXuat,
          };
        });
      } catch (e) {
        if (manageLoading) {
          this.endLoading();
        }
        swalNotification(
          getErrorMsg(e, "Có lỗi khi lấy thông tin biên bản"),
          "error"
        );
      }
    },

    async fetchToaXe(manageLoading = true) {
      try {
        const {
          data: {
            data: { ten_toa_xe, ma_toa_xe, so_hieu, danh_muc_toa_xe },
          },
        } = await ToaXeService.get(this.toaXeId);

        this.soHieuToaXe = so_hieu || ma_toa_xe;
        this.tenToaXe = `${ma_toa_xe} - ${ten_toa_xe}`;

        this.form.ten_toa_xe = `${ma_toa_xe} - ${ten_toa_xe}`;

        if (danh_muc_toa_xe.length) {
          this.danhMucToaXeId = danh_muc_toa_xe[0].id;
          this.tenDanhMucToaXe = danh_muc_toa_xe[0].ten_danh_muc;
        }
      } catch (e) {
        if (manageLoading) {
          this.endLoading();
        }
        await swalErrNotification(
          e,
          "Có lỗi xảy ra, không thể lấy thông tin toa xe này"
        );
      }
    },

    onAddedVatTu({
      selectingVatTuItems,
    }: {
      selectingVatTuItems: IVatTuResponseBase[];
    }) {
      const newVatTuItems: IVatTuWithSoLuongDaXuat[] = selectingVatTuItems.map(
        (item) => ({
          ...item,
          so_luong_da_xuat_kho: 0,
          ghi_chu: "",
        })
      );

      this.vatTuItems.push(...newVatTuItems);

      this.isDinhMucDongModalOpen = false;
    },

    onRemovingVatTu(id: number) {
      this.vatTuItems = this.vatTuItems.filter(
        ({ id: idVatTu }) => idVatTu !== id
      );
    },

    ghiChuUpdateHandler({ ghi_chu, id }: { ghi_chu: string; id: number }) {
      const vatTuItem = this.vatTuItems.find(
        ({ id: idVatTu }) => idVatTu === id
      );

      if (vatTuItem && instanceOfIVatTuWithSoLuongDaXuat(vatTuItem)) {
        vatTuItem.ghi_chu = ghi_chu;
      }
    },

    soLuongChangedHandler({
      selectingVatTu,
      so_luong_moi,
    }: {
      selectingVatTu: IVatTuWithSoLuongDaXuat;
      so_luong_moi: number;
    }) {
      const vatTuItem = this.vatTuItems.find(
        (item) => item.id === selectingVatTu.id
      );

      if (vatTuItem && instanceOfIVatTuWithSoLuongDaXuat(vatTuItem)) {
        vatTuItem.so_luong = so_luong_moi;
      }
    },

    async submit() {
      if (this.form.has_dinh_muc_dong && !this.vatTuItems.length) {
        swalNotification("Phải nhập vật tư trong định mức động", "error");

        return;
      }

      if (!this.formRef) {
        return;
      }

      if (!this.toaXeId) {
        this.form.ten_toa_xe = "";
      }

      this.formRef.validate(async (valid) => {
        if (valid) {
          this.setDialog({
            message: this.isCreating
              ? "Tạo mới BBGT với những thông tin này không?"
              : "Bạn có chắc chắn muốn lưu chỉnh sửa cho BBGT này không?",
            onConfirm: async () => {
              this.closeDialog();
              await this.startLoading();

              const buildPayload = () => ({
                so_bien_ban: this.form.so_bien_ban,
                id_ke_hoach_sua_chua:
                  this.isBelongedToKeHoach && this.keHoachDetail
                    ? this.keHoachDetail.id
                    : 0,
                id_cap_sc:
                  this.isBelongedToKeHoach && this.keHoachDetail
                    ? this.keHoachDetail.id_cap_sc
                    : this.form.cap_sua_chua_id,
                ...(this.isCreatingBienBanGiaiTheBoSung && {
                  id_cha: this.idBienBan,
                }),
                id_toa_xe: this.toaXeId,
                has_dinh_muc_dong: this.form.has_dinh_muc_dong,
                ngay_vao_xuong: formatDatePayload(this.form.ngay_vao_xuong),
                vat_tu_items: this.vatTuItems.map((item) => ({
                  ...item,
                  so_luong: +item.so_luong + +item.so_luong_da_xuat_kho,
                  ...(item.ghi_chu && {
                    ghi_chu: item.ghi_chu,
                  }),
                })),
                ...(!this.isCreating &&
                  !this.isCreatingBienBanGiaiTheBoSung && {
                    id_bien_ban: this.idBienBan,
                  }),
              });

              try {
                if (
                  (this.keHoachDetail && this.isBelongedToKeHoach) ||
                  !this.isBelongedToKeHoach
                ) {
                  const {
                    data: {
                      data: { id },
                    },
                  } = await BienBanGiaiTheService.createOrUpdate(
                    buildPayload()
                  );

                  await this.endLoading();

                  await swalNotification(
                    `${
                      this.isCreating ? "Tạo mới" : "Cập nhật"
                    } biên bản thành công`,
                    "success"
                  );

                  await this.replace(
                    `/quan-ly/bien-ban-giai-the/chi-tiet/${id}`
                  );
                }
              } catch (e) {
                this.endLoading();

                await swalNotification(getErrorMsg(e), "error");
              }
            },
          });
        } else {
          await this.focusFirstErrInputV2();

          return false;
        }
      });
    },
  },
});
